// Pure geometry helpers for the shared viewer. Side-effect-free so they
// can be unit-tested under plain `node --test` without Leaflet/MP4Box.

// Project a screen-space point onto a polyline of pre-projected pixel
// coords and return the segment whose foot-of-perpendicular best
// matches a combined spatial + temporal score.
//
// Parameters:
//   targetPx          {x, y} cursor position in layer pixels.
//   segPts            [{x, y}, …] polyline vertices, pre-projected. ≥2.
//   pts               [{t}, …] parallel array of telemetry timestamps (s).
//   aroundT           Optional reference time (typically the previous
//                     drag tick's t). Disambiguates between branches
//                     that overlap in space but are far apart in time —
//                     i.e. the forward/back legs of a figure-8 loop.
//   penaltyPxPerSec   Soft bias strength: each second of |dt| from
//                     aroundT adds this many pixels of equivalent
//                     spatial distance. 0 disables the bias.
//
// Why **linear** distance (not d²): scoring with d² makes the spatial
// gap between two parallel branches grow quadratically with cursor
// distance — so a fixed temporal bias loses its grip as the cursor
// drifts off the path, and the marker snaps to whichever branch is
// even slightly closer. Linear distance keeps the bias as a fixed
// pixel-equivalent threshold ("you must drag N pixels closer to
// switch") at every cursor distance.
//
// Returns null for degenerate inputs, otherwise:
//   { fx, fy, t, score, segIndex, f }
// where (fx, fy) is the foot-of-perpendicular in pixel space, t is
// linearly interpolated between the segment's endpoint timestamps,
// score is the combined distance + bias (lower = better), and
// f∈[0,1] is the fractional position along the segment.
export function pickNearestOnPath(
  targetPx,
  segPts,
  pts,
  aroundT,
  penaltyPxPerSec,
) {
  if (!segPts || segPts.length < 2) return null;
  const useTimeBias =
    typeof aroundT === "number" &&
    typeof penaltyPxPerSec === "number" &&
    penaltyPxPerSec > 0;
  let best = null;
  let bestScore = Infinity;
  for (let i = 1; i < segPts.length; i++) {
    const aPt = segPts[i - 1];
    const bPt = segPts[i];
    const dx = bPt.x - aPt.x;
    const dy = bPt.y - aPt.y;
    const segLen2 = dx * dx + dy * dy;
    let f = 0;
    if (segLen2 > 0) {
      f = ((targetPx.x - aPt.x) * dx + (targetPx.y - aPt.y) * dy) / segLen2;
      if (f < 0) f = 0;
      else if (f > 1) f = 1;
    }
    const fx = aPt.x + dx * f;
    const fy = aPt.y + dy * f;
    const ddx = targetPx.x - fx;
    const ddy = targetPx.y - fy;
    const spatialD = Math.sqrt(ddx * ddx + ddy * ddy);
    const tHere = pts[i - 1].t + (pts[i].t - pts[i - 1].t) * f;
    let score = spatialD;
    if (useTimeBias) {
      const dt = tHere - aroundT;
      score += penaltyPxPerSec * (dt < 0 ? -dt : dt);
    }
    if (score < bestScore) {
      bestScore = score;
      best = { fx, fy, t: tHere, score, segIndex: i - 1, f };
    }
  }
  return best;
}

// ---------- Cadence-aware baseline helpers (Plan 7 follow-up) ----------
//
// At 1 Hz cadence (legacy default), adjacent telemetry samples are tens of
// metres apart — fine for computing the heli's heading via `bearing(a, b)`
// or for drag's screen-pixel projection. At 24 Hz native cadence (Plan 7
// Task 1), adjacent samples are ~1 m / sub-pixel apart, GPS noise
// dominates the bearing, and `segLen²` in pixel space rounds to zero —
// so drag effectively stops working and the heli icon's rotation jitters.
//
// Both call sites now ask for a **macro segment** that spans ~1 second
// of telemetry regardless of native cadence. `macroSegmentEndpoints`
// returns the two sample indices the caller should use; `medianDeltaSeconds`
// gives the seed value for choosing the stride.

// Return two sample indices `{ aIdx, bIdx }` that bracket `idx` and span
// `stride` native samples in total, clamped to `[0, n-1]`. With stride=1
// this reproduces today's adjacent-sample behaviour. With stride=24 (at
// 24 Hz) the endpoints are ~1 s apart in time and ~tens-of-metres apart
// in space, which is what `bearing()` and drag's segment projection need
// to behave well.
export function macroSegmentEndpoints(idx, stride, n) {
  if (n < 2) return { aIdx: 0, bIdx: Math.max(0, n - 1) };
  const s = Math.max(1, Math.floor(stride));
  const half = Math.floor(s / 2);
  let a = idx - half;
  let b = a + s;
  if (a < 0) { a = 0; b = Math.min(n - 1, s); }
  if (b > n - 1) { b = n - 1; a = Math.max(0, b - s); }
  return { aIdx: a, bIdx: b };
}

// Median Δt (seconds) between adjacent samples. O(n) + a single sort.
// Returns 1 for degenerate inputs so callers (which divide by it) stay
// safe. Used once at viewer mount to derive `headingStride`.
export function medianDeltaSeconds(samples) {
  if (!samples || samples.length < 2) return 1;
  const deltas = [];
  for (let i = 1; i < samples.length; i++) {
    const dt = samples[i].t - samples[i - 1].t;
    if (dt > 0) deltas.push(dt);
  }
  if (deltas.length === 0) return 1;
  deltas.sort((a, b) => a - b);
  return deltas[deltas.length >> 1];
}

// Smooth lat/lon in place using a centered moving average over `window`
// samples. At 24 Hz this is a 1-second box filter — the GPS noise that
// causes the heli icon to jitter and the drag's segment-projection to
// snag on "mini sharp turns" is at ~5–20 Hz, well above the 1 Hz cutoff
// of a 24-wide boxcar.
//
// We chose moving-average over a 1-D Kalman because (a) it's deterministic
// and dependency-free, (b) the smoother only runs once at mount, and
// (c) the existing footprint baker uses the same family of filter (median
// + Hann) — so the visual cadence of the heli marker matches what the
// footprint polygon already does.
//
// The `t` axis is left untouched: scrubbing precision (and the
// `videoTimeS` written into freshly-captured frames) still uses the
// native cadence — we only smooth the displayed position.
//
// When `window <= 1` (e.g. 1 Hz GPX inputs that already arrive smoothed),
// this is a no-op so legacy behaviour is preserved exactly.
export function smoothPositionsInPlace(samples, window) {
  if (!samples) return;
  const n = samples.length;
  const w = Math.max(1, Math.floor(window));
  if (w <= 1 || n < 3) return;
  const half = Math.floor(w / 2);
  const lat = new Float64Array(n);
  const lon = new Float64Array(n);
  for (let i = 0; i < n; i++) { lat[i] = samples[i].lat; lon[i] = samples[i].lon; }
  // Prefix sums for O(n) moving average across variable window sizes near
  // the path's endpoints (where we shrink the window so endpoints don't
  // bleed into open ocean).
  const psLat = new Float64Array(n + 1);
  const psLon = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) {
    psLat[i + 1] = psLat[i] + lat[i];
    psLon[i + 1] = psLon[i] + lon[i];
  }
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - half);
    const b = Math.min(n, i + half + 1);
    const count = b - a;
    samples[i].lat = (psLat[b] - psLat[a]) / count;
    samples[i].lon = (psLon[b] - psLon[a]) / count;
  }
}

